import { useRef, useState, useCallback } from 'react';
import { useIsomorphicLayoutEffect } from '@platform-ui/hooks';
import { useFirstRender } from '@platform-ui/dom-utils';
import { getTransitionStyle, getExpandedHeight } from './utils';
export function useCollapse({
  collapsed: collapsedProp = false,
  delay = 0,
  duration = 200,
  id,
  minHeight,
  onCollapseEnd,
  onCollapseStart,
  onExpandStart,
  onExpandEnd,
  onTransitionEnd
}) {
  const el = useRef(null);
  const isFirstRender = useFirstRender();
  const isCollapsed = useRef(collapsedProp);
  const [height, setHeight] = useState(collapsedProp ? minHeight || `${0}px` : undefined);
  const [overflow, setOverflow] = useState(collapsedProp ? `hidden` : undefined);
  const handleTransitionEnd = useCallback(e => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (!collapsedProp) {
      setHeight(undefined);
      setOverflow(undefined);
      onExpandEnd == null ? void 0 : onExpandEnd();
    } else {
      onCollapseEnd == null ? void 0 : onCollapseEnd();
    }
    onTransitionEnd == null ? void 0 : onTransitionEnd(e);
  }, [collapsedProp, onTransitionEnd, onCollapseEnd, onExpandEnd]);
  useIsomorphicLayoutEffect(() => {
    if (isFirstRender) {
      return;
    }
    if (collapsedProp) {
      var _el$current;
      onCollapseStart == null ? void 0 : onCollapseStart();
      setHeight(`${(_el$current = el.current) == null ? void 0 : _el$current.scrollHeight}px`);
      setOverflow('hidden');

      /* Проставляем итоговую высоту после анимации */
      requestAnimationFrame(() => {
        setHeight(minHeight || `${0}px`);
      });
    } else {
      onExpandStart == null ? void 0 : onExpandStart();

      /* Высоту выставляем только в случаях когда блок раскрылся или закрылся */
      if (isCollapsed.current !== collapsedProp) {
        /* Необходимо для расчета высоты добавляемого контента */
        requestAnimationFrame(() => {
          var _el$current2;
          setHeight(getExpandedHeight((_el$current2 = el.current) == null ? void 0 : _el$current2.scrollHeight));
        });
      }
    }
    isCollapsed.current = collapsedProp;
  }, [collapsedProp, isFirstRender, minHeight, onCollapseStart, onExpandStart]);
  const transition = getTransitionStyle({
    height,
    overflow,
    duration,
    delay
  });
  return {
    buttonProps: {
      'aria-controls': id,
      'aria-expanded': !collapsedProp
    },
    blockProps: {
      'aria-hidden': collapsedProp,
      id,
      ref: el,
      style: {
        minHeight,
        height,
        overflow,
        transition
      },
      onTransitionEnd: handleTransitionEnd
    }
  };
}